import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserService } from '@services/core/user.service';
import { StorageService } from '@services/core/storage.service';
import { ModuleService } from '@services/core/module.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, take, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { hasSession, denyHome, denyEmptyMenu } from './auth-guard.utils';
import { ToastService } from '@services/core/toast.service';

export const authGuard: CanActivateFn = (route, state) => {
  const userService = inject(UserService)
  const storageService = inject(StorageService)
  const moduleService = inject(ModuleService)
  const toastService = inject(ToastService)
  const router = inject(Router)
  if (!hasSession(storageService)) return denyHome(router);
  return resolveAccess(userService, moduleService, toastService, router, state.url)
}

function resolveAccess(userService: UserService, moduleService: ModuleService, toast: ToastService, router: Router, url: string) {
  const menus = userService.sharingMenu();
  if (menus?.length) return fetchAccess(menus, moduleService, router, url);
  return waitForMenus(userService).pipe(
    switchMap((loaded) => decideAccess(loaded, moduleService, toast, router, url))
  );
}

function waitForMenus(userService: UserService) {
  return toObservable(userService.sharingMenu).pipe(
    filter((items: any[]) => Array.isArray(items) && items.length > 0),
    take(1),
    timeout(3000),
    catchError(() => of([] as any[]))
  );
}

function decideAccess(loaded: any[], moduleService: ModuleService, toast: ToastService, router: Router, url: string) {
  if (!loaded?.length) return of(denyEmptyMenu(router, toast));
  return fetchAccess(loaded, moduleService, router, url);
}

function fetchAccess(menus: any[], moduleService: ModuleService, router: Router, url: string) {
  return moduleService.getActiveModules().pipe(
    map((mods: any[]) => checkUrl(menus, mods || [], url, router))
  )
}

function moduleRouteMap(mods: any[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const m of mods) map.set(m.id, routeName(m))
  return map
}

function routeName(module: any): string {
  return String(module?.routeName || module?.acronym || '').toLowerCase()
}

function resolveUrl(url: string, moduleId: string, map: Map<string, string>): string {
  const clean = url.replace(/^\//, '')
  if (!clean.includes(':route')) return clean
  return clean.replace(':route', map.get(moduleId) || '')
}

function collectUrls(item: any, map: Map<string, string>, urls: string[]) {
  if (item.url) urls.push(resolveUrl(item.url, item.moduleId, map))
  pushSubs(item, map, urls)
}

function pushSubs(item: any, map: Map<string, string>, urls: string[]) {
  if (item.submenu?.length) urls.push(...flattenMenuUrls(item.submenu, map))
}

function flattenMenuUrls(items: any[], map: Map<string, string>): string[] {
  const urls: string[] = []
  for (const item of items) collectUrls(item, map, urls)
  return urls
}

function lowerList(list: string[]): string[] {
  return list.map((name) => name.toLowerCase())
}

function checkUrl(menus: any[], mods: any[], url: string, router: Router) {
  const allowed = flattenMenuUrls(menus, moduleRouteMap(mods))
  const requested = cleanRequested(url)
  if (lowerList(allowed).includes(requested)) return true
  return router.parseUrl(firstEvalUrl(allowed) || '/')
}

function cleanRequested(url: string): string {
  return url.replace(/^\//, '').split('?')[0].toLowerCase()
}

function firstEvalUrl(urls: string[]): string | null {
  const target = pickEval(urls)
  if (target) return '/' + target;
  return null
}

function pickEval(urls: string[]): string | undefined {
  return urls.find((name) => name.includes('/')) || urls[0]
}
